/**
 * extractKeySpecs — Flattens nested GearMaster specifications into
 * display-friendly {label, value} pairs, ordered by productType relevance.
 *
 * Used by: AnswerCard (sentence 1), JSON-LD Product, spec table, SEO meta.
 */

export interface KeySpec {
  label: string;
  value: string;
}

// Helper to safely access nested properties
function get(obj: Record<string, unknown>, path: string): unknown {
  return path.split('.').reduce<unknown>((o, k) => (o && typeof o === 'object' ? (o as Record<string, unknown>)[k] : undefined), obj);
}

function formatWeight(weight: unknown): string | null {
  if (!weight || typeof weight !== 'object') return null;
  const w = weight as { value?: number; unit?: string };
  if (w.value == null) return null;
  return `${w.value}${w.unit || 'g'}`;
}

function formatArray(val: unknown): string | null {
  if (!Array.isArray(val) || val.length === 0) return null;
  return val.join(', ');
}

function str(val: unknown): string | null {
  if (val == null) return null;
  const s = String(val);
  return s.length > 0 ? s : null;
}

// --- Camera spec extraction (mirrorless, DSLR, cinema, etc.) ---

function extractCameraSpecs(specs: Record<string, unknown>): KeySpec[] {
  const result: KeySpec[] = [];
  const push = (label: string, value: string | null) => {
    if (value) result.push({ label, value });
  };

  push('Type', str(get(specs, 'type')));
  push('Sensor', str(get(specs, 'sensor.type')));
  push('Resolution', str(get(specs, 'sensor.resolution')));
  push('Processor', str(get(specs, 'sensor.imageProcessor')));

  // ISO range
  const isoMin = get(specs, 'iso.min');
  const isoMax = get(specs, 'iso.max');
  if (isoMin != null && isoMax != null) {
    push('ISO Range', `${isoMin} – ${isoMax}`);
  }

  // Autofocus
  push('AF System', formatArray(get(specs, 'autofocus.system')));
  push('AF Points', str(get(specs, 'autofocus.focusPoints')));

  // Photo
  push('Max Photo Resolution', str(get(specs, 'photoSpecs.maxResolution')));
  const burstFps = get(specs, 'photoSpecs.maxBurstFrameRate');
  if (burstFps != null) push('Burst Rate', `${burstFps} fps`);

  // Video
  push('Max Video Resolution', str(get(specs, 'videoSpecs.maxResolution')));
  push('Max Frame Rate', str(get(specs, 'videoSpecs.maxFrameRate')));
  push('Bit Depth', str(get(specs, 'videoSpecs.bitDepth')));
  push('Video Color Profiles', formatArray(get(specs, 'videoSpecs.colorProfiles')));

  // Stabilization
  push('Image Stabilization', str(get(specs, 'imageStabilization.type')));

  // Display
  const displayType = str(get(specs, 'display.type'));
  const displaySize = str(get(specs, 'display.size'));
  if (displayType && displaySize) {
    push('Display', `${displaySize} ${displayType}`);
  } else {
    push('Display', displayType || displaySize);
  }
  push('Display Resolution', str(get(specs, 'display.resolution')));

  // Viewfinder
  push('Viewfinder', str(get(specs, 'viewfinder.type')));
  push('Viewfinder Resolution', str(get(specs, 'viewfinder.resolution')));

  // Storage
  push('Storage Media', formatArray(get(specs, 'storageMedia')));

  // Connectivity
  const conn: string[] = [];
  if (get(specs, 'connectivity.wifi')) conn.push('Wi-Fi');
  if (get(specs, 'connectivity.bluetooth')) conn.push('Bluetooth');
  if (get(specs, 'connectivity.usbC')) conn.push('USB-C');
  if (conn.length > 0) push('Connectivity', conn.join(', '));
  push('HDMI', str(get(specs, 'connectivity.hdmiOutput')));

  // Battery
  push('Battery', str(get(specs, 'battery.type')));
  push('Battery Life', str(get(specs, 'battery.batteryLife')));

  // Body
  push('Weather Sealing', str(get(specs, 'bodyDesign.weatherSealing')));
  push('Dimensions', str(get(specs, 'bodyDesign.dimensions')));
  push('Weight', formatWeight(get(specs, 'bodyDesign.weight')));

  // Mount
  push('Lens Mount', formatArray(get(specs, 'mount.lens')));
  push('Hot Shoe', str(get(specs, 'mount.hotShoe')));

  // Flash
  push('Flash System', str(get(specs, 'flashSystem')));

  return result;
}

// --- Lens spec extraction (photo_lens) ---

function extractLensSpecs(specs: Record<string, unknown>): KeySpec[] {
  const result: KeySpec[] = [];
  const push = (label: string, value: string | null) => {
    if (value) result.push({ label, value });
  };

  push('Lens Type', str(get(specs, 'type')));
  push('Format', str(get(specs, 'lensFormat')));
  push('Zoom/Prime', str(get(specs, 'zoomPrime')));

  // Focal length
  const minFL = get(specs, 'minFocalLength') as number | undefined;
  const maxFL = get(specs, 'maxFocalLength') as number | undefined;
  if (minFL != null && maxFL != null) {
    if (minFL === maxFL) {
      push('Focal Length', `${minFL}mm`);
    } else {
      push('Focal Length', `${minFL}–${maxFL}mm`);
    }
  } else if (minFL != null) {
    push('Focal Length', `${minFL}mm`);
  }

  // Aperture
  const aperture = get(specs, 'maximumAperture');
  if (aperture != null) push('Maximum Aperture', `f/${aperture}`);

  push('Camera System', str(get(specs, 'cameraSystem')));
  push('Lens Mount', formatArray(get(specs, 'mount.lens')));

  // Autofocus
  const af = get(specs, 'autoFocus');
  if (af != null) push('Autofocus', af ? 'Yes' : 'No');
  push('Focus Type', formatArray(get(specs, 'focusType')));

  push('Image Stabilization', str(get(specs, 'imageStabilization')));

  // Filter thread
  const filterSize = get(specs, 'filterThreadSize');
  if (filterSize != null) push('Filter Thread', `${filterSize}mm`);

  push('Weight', formatWeight(get(specs, 'weight')));

  return result;
}

// --- Cinema lens spec extraction ---

function extractCinemaLensSpecs(specs: Record<string, unknown>): KeySpec[] {
  // Cinema lenses share lens structure but emphasize different fields
  const result = extractLensSpecs(specs);

  // Cinema-specific emphasis: T-stop, focus throw, front diameter
  const push = (label: string, value: string | null) => {
    if (value) result.push({ label, value });
  };
  push('Focus Throw', str(get(specs, 'focusThrow')));
  push('Front Diameter', str(get(specs, 'frontDiameter')));

  return result;
}

// --- Generic/accessory spec extraction (fallback) ---

function extractGenericSpecs(specs: Record<string, unknown>): KeySpec[] {
  const result: KeySpec[] = [];

  function flatten(obj: Record<string, unknown>, prefix = '') {
    for (const [key, val] of Object.entries(obj)) {
      if (val == null) continue;
      const label = prefix ? `${prefix} ${key}` : key;

      if (typeof val === 'object' && !Array.isArray(val)) {
        flatten(val as Record<string, unknown>, label);
      } else if (Array.isArray(val)) {
        const formatted = formatArray(val);
        if (formatted) {
          result.push({
            label: label.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim(),
            value: formatted,
          });
        }
      } else {
        const s = str(val);
        if (s && s !== 'null') {
          result.push({
            label: label.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim(),
            value: typeof val === 'boolean' ? (val ? 'Yes' : 'No') : s,
          });
        }
      }
    }
  }

  flatten(specs);
  return result;
}

// --- Product type to extractor mapping ---

const CAMERA_TYPES = new Set([
  'mirrorless_camera', 'dslr_camera', 'cinema_camera', 'medium_format_camera',
  'compact_camera', 'film_camera', 'action_camera', 'broadcast_camera',
  'camcorder', '3d_360_camera', 'other_camera',
]);

/**
 * Extract display-friendly key specs from the nested specifications object.
 *
 * @param specifications - The raw specifications object from Firestore
 * @param productType - The product type (e.g., 'mirrorless_camera', 'photo_lens')
 * @returns Array of {label, value} pairs ordered by relevance
 */
export function extractKeySpecs(
  specifications: Record<string, unknown> | undefined | null,
  productType?: string
): KeySpec[] {
  if (!specifications || typeof specifications !== 'object') return [];

  const specs = specifications as Record<string, unknown>;

  if (productType === 'cinema_lens') {
    return extractCinemaLensSpecs(specs);
  }

  if (productType === 'photo_lens') {
    return extractLensSpecs(specs);
  }

  if (productType && CAMERA_TYPES.has(productType)) {
    return extractCameraSpecs(specs);
  }

  // For unknown product types or accessories, use generic flattener
  return extractGenericSpecs(specs);
}

/**
 * The exact spec rows a /m/ page's Specifications section displays:
 * extractKeySpecs output with the top-level `useCase` list prepended as a
 * "Use Cases" row. This is the single source of truth for "does this master
 * have renderable specs?" — SpecificationsSection renders these rows, and
 * MasterProductsPageV2 gates the section + heading + StickyTabs tab on
 * `.length > 0`. Keeping both callers on one function guarantees the tab,
 * heading, and content never disagree (e.g. a nested-spec master with <3
 * top-level keys still yields rows and stays visible).
 */
export function getMasterSpecRows(master: {
  specifications?: unknown;
  productType?: string;
  useCase?: unknown;
}): KeySpec[] {
  const rows = extractKeySpecs(
    master.specifications as Record<string, unknown> | undefined,
    master.productType
  );
  const useCases = master.useCase;
  if (Array.isArray(useCases) && useCases.length > 0) {
    return [{ label: 'Use Cases', value: useCases.join(', ') }, ...rows];
  }
  return rows;
}
